import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  V2DrawerHeader,
  V2DrawerRow,
  V2DrawerSubGroup,
  V2DrawerSubRow,
  V2DrawerUnavailableRow,
  V2DrawerSectionLabel,
  Icon,
  LoadingState,
  useTheme,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { DESKTOP_NAV_GROUPS, isNavEntryEntitled, type NavEntry } from '../../shell/navConfig.js';
import { MASTER_DATA_ENTITIES } from '../../lib/masterDataConfig.js';
import { canSeeNavEntry, canSeeTab } from '../../shell/navAccess.js';
import { useAccessContext } from '../../shell/useNavAccess.js';
import type { ShellContextResponse } from '../../lib/types/shell.js';
import { QuickCalendarPopover } from '../../shell/topbar/QuickCalendarPopover.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Mobile App.dc.html §FRAME 08 مینو — نسخهٔ موبایل نوار
 *   کناری / MOBILE MENU DRAWER
 * canonical pattern: full module list — the mobile-shell equivalent of the
 *   desktop sidebar, same 52px rows, active-item edge bar, nested-group
 *   expansion (اطلاعات پایه → کالا/طرف‌حساب/واحد سنجش/مالیات/انبار — the
 *   real MASTER_DATA_ENTITIES config, not the board's own item order),
 *   and an explained-reason state for an unentitled module instead of a
 *   bare hidden or disabled row.
 * archetype: rendered as a routed page (apps/client's existing
 *   `/mobile/menu`), not the board's overlay+backdrop — the row markup is
 *   identical either way (see `V2Drawer` in the design-system for the
 *   overlay variant, not yet wired in here).
 * status: KIT_CONFORMANT — no per-user display name exists in the API
 *   (same gap as AuthenticatedShell's topbar) and no per-module
 *   unavailable-reason copy exists from the backend, so one honest generic
 *   reason is used for every unentitled item rather than the board's
 *   bespoke example strings.
 */
/** The routed page (deep link / fallback) — the same panel the bottom-nav drawer opens. */
export function MenuPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  if (!slug) return null;
  return (
    <MobileMenuPanel
      slug={slug}
      onClose={() => (window.history.length > 1 ? navigate(-1) : navigate(tenantBasePath(slug)))}
    />
  );
}

/**
 * The menu rows (modules, nested «اطلاعات پایه», theme, calendar, account).
 * `onNavigate` lets the drawer close itself when a link is followed; `onClose`
 * adds the header's close button.
 */
export function MobileMenuPanel({
  slug,
  onNavigate,
  onClose,
}: {
  readonly slug: string;
  readonly onNavigate?: () => void;
  readonly onClose?: () => void;
}) {
  const location = useLocation();
  const { mode, setMode } = useTheme();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ 'master-data': true });
  const context = useApiGet<ShellContextResponse>(
    slug ? `${tenantBasePath(slug)}/api/v1/admin/shell-context` : null,
    [slug],
  );
  const access = useAccessContext();

  const base = tenantBasePath(slug);
  if (context.status !== 'ready' || context.data.phase !== 'ready' || !context.data.entitlements) {
    return <LoadingState />;
  }

  const entitledModuleSlugs = context.data.entitlements.entitledModuleSlugs;
  const isActive = (entry: NavEntry) => location.pathname === `${base}${entry.hrefSuffix}`;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      onClick={(event) => {
        // Following any link closes the drawer; rows that only expand do not.
        if ((event.target as HTMLElement).closest('a[href]')) onNavigate?.();
      }}
    >
      <V2DrawerHeader
        tenantName={slug}
        tenantSub="فضای کاری سازمان"
        {...(onClose ? { onClose } : {})}
      />
      <div className="sk-drawer__nav" style={{ flex: 1 }}>
        {DESKTOP_NAV_GROUPS.flatMap((group) => group.items)
          .filter((entry) => canSeeNavEntry(entry.id, access))
          .map((entry) => {
            const entitled = isNavEntryEntitled(entry, entitledModuleSlugs);
            if (!entitled) {
              return (
                <V2DrawerUnavailableRow
                  key={entry.id}
                  icon={entry.icon}
                  label={entry.label}
                  reason="این بخش برای دسترسی شما در این سازمان فعال نیست."
                />
              );
            }
            if (entry.id === 'master-data') {
              const isOpen = expanded['master-data'] ?? false;
              return (
                <div key={entry.id}>
                  <V2DrawerRow
                    icon={entry.icon}
                    label={entry.label}
                    active={isActive(entry)}
                    onClick={() => setExpanded((prev) => ({ ...prev, 'master-data': !isOpen }))}
                    trailing={<Icon name="chevronDown" size="sm" />}
                  />
                  {isOpen ? (
                    <V2DrawerSubGroup>
                      {MASTER_DATA_ENTITIES.filter((entity) =>
                        canSeeTab('master-data', entity.kind, access),
                      ).map((entity) => (
                        <Link
                          key={entity.kind}
                          to={`${base}/master-data/${entity.pathSegment}`}
                          style={{ textDecoration: 'none' }}
                        >
                          <V2DrawerSubRow
                            label={entity.label}
                            active={
                              location.pathname === `${base}/master-data/${entity.pathSegment}`
                            }
                          />
                        </Link>
                      ))}
                    </V2DrawerSubGroup>
                  ) : null}
                </div>
              );
            }
            return (
              <Link
                key={entry.id}
                to={`${base}${entry.hrefSuffix}`}
                style={{ textDecoration: 'none' }}
              >
                <V2DrawerRow icon={entry.icon} label={entry.label} active={isActive(entry)} />
              </Link>
            );
          })}
        <V2DrawerSectionLabel>سایر</V2DrawerSectionLabel>
        <Link to={`${base}/account`} style={{ textDecoration: 'none' }}>
          <V2DrawerRow icon="user" label="حساب کاربری" />
        </Link>
        {/* Topbar utilities with no bottom-nav route of their own (theme
            toggle, quick calendar) — moved here instead of the topbar,
            which has no room for them on mobile (see components.css's
            .sk-topbar__actions mobile rule). */}
        <V2DrawerRow
          icon={mode === 'dark' ? 'moon' : 'sun'}
          label="حالت نمایش"
          trailing={
            <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
              {mode === 'dark' ? 'تاریک' : 'روشن'}
            </span>
          }
          onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
        />
        <V2DrawerRow
          icon="calendar"
          label="تقویم سریع"
          active={expanded['calendar'] ?? false}
          trailing={<Icon name="chevronDown" size="sm" />}
          onClick={() => setExpanded((prev) => ({ ...prev, calendar: !prev['calendar'] }))}
        />
        {expanded['calendar'] && slug ? (
          <QuickCalendarPopover slug={slug} open={expanded['calendar'] ?? false} />
        ) : null}
      </div>
    </div>
  );
}
